/*
 * Copyright 2018-2025 CommScope, Inc., All rights reserved.
 *
 * This program is confidential and proprietary to CommScope, Inc. (CommScope), and
 * may not be copied, reproduced, modified, disclosed to others, published or used, in
 * whole or in part, without the express prior written permission of CommScope.
 */

import { call, getContext, put, select, takeLatest } from 'redux-saga/effects';
import { get } from 'lodash';
import moment from 'moment';
import { rootNodeSelector } from 'app/redux/hierarchy';
import { ajaxGet } from 'app/redux/util-sagas';
import { createErrorAction } from 'app/redux/utils';
import {
  fetchSMBProWifiReportComplete,
  fetchSMBProWifiReportError,
  updateSMBProWifiReportFetchState,
  reportError
} from '.';

const url = '/api/cdbapi/getSMBProWifiReport';

export function* fetchSMBProWifiReportSaga({ payload }) {
  yield put(
    updateSMBProWifiReportFetchState({ inProgress: true, failed: false })
  );

  const rootNode = yield select(rootNodeSelector);

  const { vertical, subcustomer, ...otherParams } = yield payload;
  const params = yield {
    ...otherParams,
    vertical,
    subcustomer
  };

  // Only include defined & non-empty values in query string
  const queryString = Object.entries(params)
    // eslint-disable-next-line no-unused-vars
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => {
      if (typeof value === 'object') {
        // Objects cannot be directly encoded in the URL
        return null; // Skip this parameter
      }
      return `${key}=${encodeURIComponent(value)}`;
    })
    .filter(Boolean) // Remove nulls generated by skipping objects
    .join('&');

  const finalOptions = yield {
    headers: { Accept: 'text/csv' },
    responseType: 'blob',
    onTimeout: () => put(updateSMBProWifiReportFetchState({ failed: true }))
  };

  const saveFileAs = yield getContext('saveFileAs');
  const filename =
    (rootNode.name || 'Property').toUpperCase() +
    (subcustomer ? `_${subcustomer.toUpperCase()}` : '') +
    (vertical ? `_${vertical.toUpperCase()}` : '') +
    '_SMBProWifiReport_' +
    moment().format('YYYYMMDD_HHmm') +
    '.csv';

  try {
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    const { data } = yield ajaxGet(fullUrl, finalOptions);

    if (!(data instanceof Blob)) {
      throw yield new Error('No data');
    }

    if (yield get(payload, 'download', false)) {
      yield call(saveFileAs, data, filename);
    }
    yield put(fetchSMBProWifiReportComplete(data));
    yield put(updateSMBProWifiReportFetchState({ failed: false }));
  } catch (err) {
    yield put(createErrorAction(fetchSMBProWifiReportError, err));
    yield put(updateSMBProWifiReportFetchState({ failed: true }));
    yield put(
      reportError({
        action: 'generate',
        type: 'smb-prowifi-report',
        report: { ...params },
        error: err
      })
    );
  } finally {
    yield put(updateSMBProWifiReportFetchState({ inProgress: false }));
  }
}

export default function createFetchSMBProWifiReportSagas(types) {
  return [takeLatest([types.fetchSMBProWifiReport], fetchSMBProWifiReportSaga)];
}
